import { useState } from "react";
import { Modal } from "@/components/Modal";
import { BrBox } from "@/components/BrBox";
import { BrBtn } from "@/components/BrBtn";
import { BrLabel } from "@/components/BrLabel";
import { BrTag } from "@/components/BrTag";
import { PaletteSwitcher } from "@/components/PaletteSwitcher";
import { useToast } from "@/components/ToastProvider";
import {
  useConnectChesscom,
  useMe,
  useStartLichess,
  useUpdateMe,
  offsetShadow,
  TYPE,
  formatRelativeTime,
  initialsFrom,
  type Palette,
} from "@chess/shared";
import { useAuth } from "@/auth/AuthProvider";
import { getManagementUrl } from "@/billing/purchases";
import { useTheme, useThemeControls } from "@/theme/ThemeProvider";

interface Props {
  open: boolean;
  onClose: () => void;
  onUpgrade: () => void;
}

// Defined at module scope (NOT inside SettingsModal) so their component identity
// is stable across renders. When they lived inside the component body, every
// keystroke re-created them, remounting the subtree and stealing input focus.
function Section({
  p,
  label,
  children,
  style = {},
}: {
  p: Palette;
  label: string;
  children: React.ReactNode;
  style?: React.CSSProperties;
}) {
  return (
    <div style={{ borderTop: `2px solid ${p.border}`, padding: "22px 24px", ...style }}>
      <BrLabel>{label}</BrLabel>
      <div style={{ marginTop: 14 }}>{children}</div>
    </div>
  );
}

function ToggleRow({
  p,
  label,
  hint,
  on,
  onChange,
}: {
  p: Palette;
  label: string;
  hint: string;
  on: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 0", borderBottom: `1px dashed ${p.border}`, gap: 14 }}>
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ fontSize: 13.5, fontWeight: 700 }}>{label}</div>
        <div style={{ fontSize: 11.5, color: p.fgMuted, marginTop: 2 }}>{hint}</div>
      </div>
      <button
        onClick={() => onChange(!on)}
        style={{
          width: 56,
          height: 30,
          background: on ? p.success : p.bg,
          border: `2px solid ${p.border}`,
          boxShadow: offsetShadow(p, 2, 2),
          position: "relative",
          cursor: "pointer",
          flexShrink: 0,
        }}
      >
        <div
          style={{
            position: "absolute",
            top: 1,
            left: on ? 26 : 1,
            width: 22,
            height: 22,
            background: p.fg,
            border: `2px solid ${p.border}`,
            transition: "left .15s",
          }}
        />
      </button>
    </div>
  );
}

export function SettingsModal({ open, onClose, onUpgrade }: Props) {
  const p = useTheme();
  const { themeId, setThemeId } = useThemeControls();
  const updateMe = useUpdateMe();
  const { data: me, refetch } = useMe();
  const connectChesscom = useConnectChesscom();
  const startLichess = useStartLichess();
  const toast = useToast();
  const auth = useAuth();

  const [chesscomUsername, setChesscomUsername] = useState("");
  const [notifs, setNotifs] = useState({ email: true, push: false, weekly: true });

  const chesscom = me?.connected_accounts.find((a) => a.provider === "chesscom") ?? null;
  const lichess = me?.connected_accounts.find((a) => a.provider === "lichess") ?? null;

  async function handleConnectChesscom() {
    try {
      await connectChesscom.mutateAsync({ username: chesscomUsername.trim() });
      toast.push({ kind: "success", text: "Chess.com linked" });
      setChesscomUsername("");
      await refetch();
    } catch (err) {
      toast.push({ kind: "danger", text: err instanceof Error ? err.message : "Failed" });
    }
  }

  async function handleConnectLichess() {
    try {
      const r = await startLichess.mutateAsync();
      window.location.href = r.authorize_url;
    } catch (err) {
      toast.push({ kind: "danger", text: err instanceof Error ? err.message : "Failed" });
    }
  }

  async function handleSignOut() {
    await auth.signOut();
    onClose();
  }

  const isPro = (me?.effective_tier ?? "free") !== "free";

  async function handleManageSubscription() {
    try {
      const url = await getManagementUrl();
      if (url) window.open(url, "_blank", "noopener");
      else toast.push({ kind: "danger", text: "No subscription portal available for this account." });
    } catch {
      toast.push({ kind: "danger", text: "Could not open the subscription portal." });
    }
  }

  return (
    <Modal open={open} onClose={onClose} width={680} label="SETTINGS">
      <div style={{ padding: "28px 24px 18px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <div
            style={{
              width: 56,
              height: 56,
              background: p.accent,
              color: p.accentFg,
              border: `2px solid ${p.border}`,
              boxShadow: offsetShadow(p, 3, 3),
              display: "grid",
              placeItems: "center",
              fontSize: 18,
              fontWeight: 800,
            }}
          >
            {initialsFrom(me?.display_name ?? me?.email)}
          </div>
          <div>
            <div style={{ fontFamily: TYPE.display, fontSize: 30, fontWeight: 800, letterSpacing: -1, lineHeight: 1, textTransform: "uppercase" }}>
              {(me?.display_name ?? me?.email ?? "Player").toString()}
            </div>
            <div style={{ fontSize: 12, color: p.fgMuted, fontFamily: TYPE.mono, marginTop: 4, letterSpacing: 0.5 }}>
              {me?.email ?? ""}
            </div>
          </div>
        </div>
      </div>

      <Section p={p} label="APPEARANCE">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          <div>
            <div style={{ fontSize: 13.5, fontWeight: 700 }}>Color palette</div>
            <div style={{ fontSize: 11.5, color: p.fgMuted, marginTop: 2 }}>Saved to your profile — syncs across devices.</div>
          </div>
          <PaletteSwitcher
            themeId={themeId}
            onChange={(id) => {
              setThemeId(id);
              if (auth.signedIn) updateMe.mutate({ theme: id });
            }}
          />
        </div>
      </Section>

      <Section p={p} label="PLAN">
        <BrBox padding={16} shadowSize={4} bg={p.bg}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8, flexWrap: "wrap", gap: 10 }}>
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ fontFamily: TYPE.display, fontSize: 24, fontWeight: 800, textTransform: "uppercase", letterSpacing: -0.5 }}>
                  {(me?.effective_tier ?? "free").toUpperCase()} PLAN
                </span>
                <BrTag>CURRENT</BrTag>
              </div>
              <div style={{ fontSize: 12, color: p.fgMuted, marginTop: 4 }}>
                {isPro
                  ? "Full history · depth 22 analysis · unlimited routines"
                  : "Depth 15 analysis · 3 routines · 50-game backfill"}
              </div>
            </div>
            {isPro ? (
              <BrBtn size="sm" variant="ghost" withShadow={false} onClick={handleManageSubscription}>
                Manage subscription →
              </BrBtn>
            ) : (
              <BrBtn size="sm" variant="accent" onClick={onUpgrade}>Upgrade to Pro →</BrBtn>
            )}
          </div>
        </BrBox>
      </Section>

      <Section p={p} label="CONNECTED ACCOUNTS">
        <ConnectRow
          name="CHESS.COM"
          subtitle={
            chesscom
              ? `Linked as ${chesscom.external_username} · last synced ${formatRelativeTime(chesscom.last_synced_at)}`
              : "Not connected — username only, no password"
          }
          color={p.success}
          connected={!!chesscom}
        >
          {!chesscom && (
            <div style={{ display: "flex", gap: 8, width: "100%", marginTop: 12 }}>
              <input
                value={chesscomUsername}
                onChange={(e) => setChesscomUsername(e.target.value)}
                placeholder="username"
                style={{
                  flex: 1,
                  padding: "8px 10px",
                  background: p.card,
                  color: p.fg,
                  border: `2px solid ${p.border}`,
                  fontFamily: TYPE.mono,
                  fontSize: 13,
                  fontWeight: 700,
                  outline: "none",
                }}
              />
              <BrBtn size="sm" variant="accent" onClick={handleConnectChesscom} disabled={connectChesscom.isPending || chesscomUsername.trim().length === 0}>
                {connectChesscom.isPending ? "Linking…" : "Connect →"}
              </BrBtn>
            </div>
          )}
        </ConnectRow>

        <ConnectRow
          name="LICHESS"
          subtitle={
            lichess
              ? `Linked as ${lichess.external_username} · last synced ${formatRelativeTime(lichess.last_synced_at)}`
              : "OAuth — opens lichess.org"
          }
          color={p.info}
          connected={!!lichess}
        >
          {!lichess && (
            <BrBtn size="sm" variant="accent" onClick={handleConnectLichess} disabled={startLichess.isPending}>
              {startLichess.isPending ? "Opening…" : "Connect →"}
            </BrBtn>
          )}
        </ConnectRow>
      </Section>

      <Section p={p} label="PREFERENCES">
        <ToggleRow p={p} label="Email me daily training reminders" hint="One per day at your scheduled time" on={notifs.email} onChange={(v) => setNotifs({ ...notifs, email: v })} />
        <ToggleRow p={p} label="Push notifications" hint="Mobile + browser" on={notifs.push} onChange={(v) => setNotifs({ ...notifs, push: v })} />
        <ToggleRow p={p} label="Weekly review digest" hint="Sundays · what improved + what's still leaking rating" on={notifs.weekly} onChange={(v) => setNotifs({ ...notifs, weekly: v })} />
      </Section>

      <Section p={p} label="ACCOUNT" style={{ borderBottom: `2px solid ${p.border}` }}>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <div style={{ flex: 1 }} />
          <BrBtn size="sm" variant="ghost" withShadow={false} onClick={handleSignOut}>Sign out</BrBtn>
        </div>
      </Section>
    </Modal>
  );
}

function ConnectRow({
  name,
  subtitle,
  color,
  connected,
  children,
}: {
  name: string;
  subtitle: string;
  color: string;
  connected: boolean;
  children?: React.ReactNode;
}) {
  const p = useTheme();
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 14,
        padding: 14,
        border: `2px solid ${p.border}`,
        boxShadow: offsetShadow(p, 3, 3),
        background: p.bg,
        marginBottom: 12,
        flexWrap: "wrap",
      }}
    >
      <div
        style={{
          width: 44,
          height: 44,
          background: color,
          color: p.accentFg,
          border: `2px solid ${p.border}`,
          display: "grid",
          placeItems: "center",
          fontSize: 22,
          fontWeight: 800,
          fontFamily: TYPE.mono,
          flexShrink: 0,
        }}
      >
        ♚
      </div>
      <div style={{ flex: 1, minWidth: 200 }}>
        <div style={{ fontSize: 15, fontWeight: 800, textTransform: "uppercase", letterSpacing: 0.3 }}>{name}</div>
        <div style={{ fontSize: 11.5, color: p.fgMuted, fontFamily: TYPE.mono, marginTop: 2, letterSpacing: 0.5 }}>{subtitle}</div>
      </div>
      {connected && <BrTag bg={p.success} fg={p.successFg}>● LIVE</BrTag>}
      {children}
    </div>
  );
}
